import React from "react";
import { Box, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BaseDataGrid from "components/base/BaseDataGrid";
import SoothCard from "components/base/SoothCard";
import AccessibleIconButton from "components/base/AccessibleIconButton";

export default function TopArticlesDataGrid({ articles, isLoading }) {
  const rows = React.useMemo(
    () =>
      articles
        ? articles.map((a, idx) => ({
            id: idx + 1,
            views:
              a.views >= 1000
                ? `${(a.views / 1000).toFixed(1)}K`
                : a.views.toString(),
            title: a.title,
            date: a.date,
            url: a.url || null,
          }))
        : [],
    [articles]
  );

  // define columns
  const columns = React.useMemo(
    () => [
      {
        field: "views",
        headerName: "Views",
        resizable: false,
        width: 100,
        sortable: false,
      },
      {
        field: "title",
        headerName: "Title",
        resizable: false,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Typography
            variant="inherit"
            noWrap
            title={params.value}
            sx={{
              cursor: "pointer",
              "&:hover": {
                color: "primary.main",
                textDecoration: "underline",
              },
            }}
            onClick={() => {
              if (params.row.url) {
                window.open(params.row.url, "_blank");
              }
            }}
          >
            {params.value}
          </Typography>
        ),
      },
    ],
    []
  );

  return (
    <SoothCard
      sx={{
        backgroundColor: (theme) => theme.palette.secondary.main,
        borderRadius: 2,
        p: 2,
        overflow: "hidden",
        ...(isLoading && { height: 500 }),
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          mb: 2,
        }}
      >
        <Typography variant="h4" sx={{ textAlign: "center" }}>
          Top Articles
        </Typography>
        <AccessibleIconButton
          size="small"
          sx={{ p: 0.5 }}
          title="Articles that received the highest engagement and views from readers during the selected timeframe."
          tooltipProps={{
            placement: "top",
          }}
        >
          <InfoOutlinedIcon fontSize="small" color="primary" />
        </AccessibleIconButton>
      </Box>
      <Box sx={{ height: isLoading ? 400 : "auto" }}>
        <BaseDataGrid
          loading={isLoading}
          rows={rows}
          rowHeight={60}
          disableColumnMenu={true}
          columns={columns}
          autoHeight={!isLoading}
          hideFooter
          disableSelectionOnClick
          slotProps={{
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
          }}
          sx={{
            border: "none",
            ".MuiDataGrid-cell": {
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            },
            ".MuiDataGrid-columnHeaders": {
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            },
          }}
        />
      </Box>
    </SoothCard>
  );
}
